import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useTexte } from '../texte'
import { ensureSettings, ensureTrip, useDiscardMedia, useUploadMedia } from './queries'
import { api, ApiError, netzStand } from '../api/client'
import type { Media, Settings } from '../api/types'
import { bildgrenzen, canPreviewDirectly, isImage, prepareImage } from '../lib/images'
import { isVideo, standbildAdresse, uploadVideo, videoZiel } from '../lib/video'
import { ausbeuteText, kannWandeln } from '../lib/videoNames'
import { notiere } from '../lib/sendeprotokoll'
import { eingabeAendern, type Attachment, type Stufe } from '../lib/eingabevorrat'
import { ueberallAbspielbar, type Aufnahme } from '../lib/sprachaufnahme'
import type { Uhr } from '../lib/ortszeit'

/** Der Schlüssel einer Kachel -- je Sitzung fortlaufend, nie zweimal derselbe. */
let naechsterSchluessel = 1

/**
 * What hangs on the message being written: the tiles, and everything that
 * fills them.
 *
 * Pulled out of `Composer.tsx` in 1.23.0, where it was the largest single
 * block of a 1953-line function. Three ways lead in -- a file from the
 * picker, a GIF the server already fetched, a finished voice recording --
 * and all three end in the same list, with the same stages, on the same
 * chain. That is the reason for one place: a fourth way would otherwise be
 * a fourth copy of "tile, work, upload, say what went wrong".
 *
 * The line itself keeps what is not an attachment: the text, the store per
 * place (text and tiles travel together there), sending.
 */
export function useAttachments({
  tripId,
  reiseVersatz,
}: {
  tripId: number
  /** Falls back for `add()` while the trip query is still in flight (#59). */
  /** The trip's clock -- a chat photo is dated by it as well. */
  reiseVersatz: Uhr
}) {
  const t = useTexte()
  const [fotos, setFotos] = useState<Attachment[]>([])
  const [problem, setProblemRoh] = useState<string | null>(null)
  /**
   * Whether the sentence is about the missing net (1.80.0): the one message
   * line shows it red and takes it away when the net comes back.
   */
  const [problemOhneNetz, setProblemOhneNetz] = useState(false)
  function setProblem(text: string | null, wegenNetz = false) {
    setProblemRoh(text)
    setProblemOhneNetz(text !== null && wegenNetz)
  }
  // Die Uploads gehen nacheinander, aufgereiht an dieser Kette. Das nächste
  // Bild wird schon verkleinert, während das vorige über die Leitung geht --
  // Rechnen und Senden überlappen, und beides ist auf einem Telefon der
  // Flaschenhals.
  const kette = useRef<Promise<void>>(Promise.resolve())
  /**
   * The cancel of a video's upload, per tile (1.176.1): a video waiting for
   * the net can be taken out, and its upload must then stop instead of
   * finishing into nothing when the net comes back.
   */
  const cancels = useRef(new Map<number, { abgebrochen: boolean; id?: number }>())

  const upload = useUploadMedia(tripId)
  const discard = useDiscardMedia()
  const client = useQueryClient()
  // „Beschäftigt" heißt: da läuft noch etwas, warte damit. Ein Bild, das auf
  // Netz wartet, läuft gerade **nicht** -- es wäre sonst für immer beschäftigt
  // und der Beitrag ginge nie in den Korb.
  const beschaeftigt = fotos.some((foto) => unterwegs(foto.stufe))
  // Abgeleitet und nicht gemerkt, und das ist die Korrektur eines Fehlers:
  // vorher stand hier ein eigener Zustand, der beim Auswählen eines Videos auf
  // `true` ging und **nirgends** zurück. Der Hinweis blieb damit stehen, auch
  // nachdem die Nachricht längst raus war -- bis jemand den Chat verließ und
  // neu betrat. Was aus den Anhängen ablesbar ist, gehört nicht daneben noch
  // einmal aufgeschrieben.
  const fertige = fotos.filter((foto) => foto.stufe === 'fertig')

  /**
   * Was gerade angeheftet ist -- fuer den Vorrat beim Ortswechsel und fuer
   * einen Upload, der erst nach dem Wechsel fertig wird (`aendere`).
   */
  const angeheftet = useRef<Attachment[]>([])
  angeheftet.current = fotos

  /** Wie viele Kacheln gerade arbeiten -- für den Satz „3 von 5". */
  const wartet = fotos.filter((foto) => unterwegs(foto.stufe)).length

  /**
   * Ob diese Datei über der Grenze liegt -- und über welcher.
   *
   * Die Grenze gilt ohnehin auf dem Server; hier steht sie nur, damit man es
   * vor dem Warten sieht statt danach. Ohne bekannte Werte wird nichts
   * behauptet.
   */
  function ueberGrenze(
    bytes: number,
    art: 'bild' | 'video' | 'ton',
    werte: Settings | null,
  ): number | undefined {
    if (!werte) return undefined
    const grenze = art === 'video' ? werte.video_max_mb : werte.image_max_mb
    return bytes > grenze * 1024 * 1024 ? grenze : undefined
  }

  function aendere(key: number, teil: Partial<Attachment>) {
    // After a switch of place the tile lies in the store, not in this list
    // (1.22.0): an upload that finishes late must not touch the new place.
    if (!angeheftet.current.some((foto) => foto.key === key) && eingabeAendern(key, teil)) return
    setFotos((alt) => alt.map((foto) => (foto.key === key ? { ...foto, ...teil } : foto)))
  }

  /**
   * Fotos annehmen -- und zwar sofort.
   *
   * Für jede Datei steht die Kachel da, bevor irgendetwas gerechnet ist. Das
   * ist der ganze Punkt: der Auswahldialog gibt die Bilder ab und man ist
   * zurück im Chat, statt vor einem stehengebliebenen Bildschirm zu warten,
   * bis fünf iPhone-Fotos verkleinert sind.
   */
  async function add(files: FileList | File[] | null) {
    if (!files || files.length === 0) return
    setProblem(null)

    /*
     * **Die Liste wird sofort festgehalten, vor jedem `await`.**
     *
     * Eine `FileList` ist keine Kopie, sondern der Blick auf das Feld — und
     * der Aufrufer setzt es unmittelbar nach dem Aufruf zurück (`value = ''`),
     * damit dieselbe Datei ein zweites Mal auslöst. Solange hier oben nichts
     * gewartet wurde, ging das gut; mit dem ersten `await` war sie leer, wenn
     * die Arbeit begann, und es geschah schlicht gar nichts.
     *
     * Beim Umbau für #59 ist genau das passiert, und der Prüfstand hat es
     * gefunden: der Sendeknopf des Mitlesenden erschien nie.
     */
    const gewaehlt = Array.from(files)

    /*
     * **Erst die Rolle, dann alles Weitere** (#59).
     *
     * Sie steht in `useTrip`, und beim ersten Öffnen eines Chats ist die
     * Abfrage noch unterwegs — wer in diesem Augenblick ein Foto auswählt,
     * gälte für einen Moment als Mitlesender. Daran hängt seit #59 die
     * Kantenlänge, mit der verkleinert wird, **und** ob ein Video überhaupt
     * durchdarf. Der Prüfstand hat genau das gemessen: das Foto des Autors
     * kam mit 800 statt 2000 Punkten an und sah dabei aus wie eines, das der
     * Einstellung folgt.
     *
     * Dieselbe Falle wie bei den Einstellungen eine Zeile weiter unten, und
     * dieselbe Antwort: gewartet wird, und zwar nur beim ersten Mal.
     */
    const [werte, reise] = await Promise.all([
      ensureSettings(client),
      ensureTrip(client, tripId),
    ])
    // One source for the role (1.112.0, review): the trip from the cache,
    // not a second answer handed in by the caller -- two sources for one
    // question is the shape of the flickering tab (#26).
    const alsReisender = reise?.role === 'sender'

    /*
     * **Ein Video eines Mitlesenden wird hier abgewiesen, nicht verschluckt.**
     * `accept` ist keine Sperre — auf iOS steht neben „Fotos" weiter „Alle
     * Dateien", und was dort ausgewählt wird, käme sonst bis zum Server und
     * würde erst dort abgelehnt: nach dem Umrechnen, nach dem Warten.
     *
     * Und es wird **gesagt**, nicht stillschweigend weggelassen. Etwas
     * auswählen und es passiert nichts — genau das soll es hier nie geben.
     */
    const videos = gewaehlt.filter((datei) => isVideo(datei))
    if (!alsReisender && videos.length > 0) {
      setProblem(
        videos.length === gewaehlt.length
          ? t.chat.nurVideosAbgewiesen
          : t.chat.videosAbgewiesen(videos.length),
      )
    }

    /*
     * **Und ohne Netz geht ein Video gar nicht -- das steht vorher da, nicht
     * hinterher** (1.45.0).
     *
     * Aus dem Betrieb, mit Bildschirmfoto: ohne Netz ein Video ausgewählt, und
     * an der Kachel stand in Rot „Dieses Gerät konnte das Video nicht
     * umrechnen (Importing a module script failed)". Der Satz stimmt nicht
     * einmal -- das Gerät kann es, es fehlte der Nachschlag für den
     * Umrechner --, und selbst wenn er gestimmt hätte, ginge ein Video ohne
     * Netz nicht hinaus: es überlebt den Ausgangskorb nicht (zweihundert
     * Megabyte gehören nicht in IndexedDB). Aus dem Betrieb dazu: „das macht
     * eigentlich auch keinen Sinn, ein Video hochzuladen, aber dann sollte es
     * gesperrt werden."
     *
     * **Am Punkt stand die Sperre längst** (`useBogenmedien.videoDazu`), im
     * Chat nicht -- diesmal andersherum als sonst, aber dieselbe Regel: eine
     * Sache wird an allen Stellen bedient.
     *
     * Ein Foto geht weiter, auch ohne Netz: es wartet im Korb.
     */
    const ohneNetz = !netzStand()
    if (ohneNetz && alsReisender && videos.length > 0) {
      setProblem(
        videos.length === gewaehlt.length
          ? t.chat.nurVideosOhneNetz
          : t.chat.videosOhneNetz(videos.length),
        true,
      )
    }

    const aufgaben = gewaehlt
      .filter((datei) => (alsReisender && !ohneNetz) || !isVideo(datei))
      .map((file) => ({
        file,
        key: naechsterSchluessel++,
      }))
    if (aufgaben.length === 0) return

    setFotos((alt) => [
      ...alt,
      ...aufgaben.map(({ file, key }) => ({
        key,
        preview: canPreviewDirectly(file) ? URL.createObjectURL(file) : null,
        stufe: 'wartet' as Stufe,
        fortschritt: 0,
        bytes: file.size,
        art: (isVideo(file) ? 'video' : 'bild') as 'bild' | 'video',
      })),
    ])

    // **Die Grenze sofort -- aber nur, wenn sie schon feststeht.**
    //
    // Kann dieser Browser umrechnen, entscheidet erst das Ergebnis, wie gross
    // die Datei ist; ein rotes „über 200 MB", das nach einer Minute wieder
    // verschwindet, wäre eine Warnung, die sich selbst zurücknimmt. Kann er
    // es nicht, geht das Original hinaus, und dann stimmt sie vom ersten
    // Augenblick an -- und wer das erst erfährt, wenn der Server ablehnt, hat
    // schon gewartet.
    for (const { file, key } of aufgaben) {
      if (isVideo(file) && !kannWandeln()) {
        aendere(key, { ueberGrenze: ueberGrenze(file.size, 'video', werte) })
      }
    }

    // Hier kommt nur noch an, was der Autor ausgewaehlt hat: die
    // Dateiauswahl steht nur ihm offen. Die Sperren gegen fremde Fotos und
    // Videos stehen weiterhin im Server -- dort, wo sie hingehoeren.
    for (const { file, key } of aufgaben) {
      // Ein Video geht einen eigenen Weg: **erst verkleinert** (#4), dann in
      // Stücken hochgeladen. Das Standbild entsteht dabei.
      //
      // Hier stand, ein Video werde nicht verkleinert, „der Browser könnte
      // es, aber es dauerte Minuten und macht das Telefon heiß". Das galt für
      // den Weg über ein `<video>`-Element, das dafür abspielen muss. Über
      // den Demuxer läuft es in Lesegeschwindigkeit.
      if (isVideo(file)) {
        // **Standbild und Dauer macht `uploadVideo` selbst** (#4). Hier
        // standen zwei eigene `<video>`-Elemente: eines fuer das Standbild,
        // eines nur fuer die Dauer -- und beide liefen los, waehrend der
        // Umrechner ebenfalls einen Dekodierer wollte. iOS gibt nur wenige
        // her. Die Dauer steht ohnehin im Befund, den `onErgebnis` bringt.
        kette.current = kette.current.then(async () => {
          const cancel: { abgebrochen: boolean; id?: number } = { abgebrochen: false }
          cancels.current.set(key, cancel)
          try {
            aendere(key, { stufe: 'rechnet', fortschritt: 0 })
            const medium = await uploadVideo(
              tripId,
              file,
              (anteil) => aendere(key, { stufe: 'lädt', fortschritt: anteil }),
              {
                signal: cancel,
                onRegistered: (id) => {
                  cancel.id = id
                },
                // From use: the error stayed after the net came back, and the
                // video never went (1.176.1). Now the tile waits, and the
                // red line goes with the missing net on its own.
                onNetWait: (waiting) => {
                  aendere(key, { waitsForNet: waiting })
                  if (waiting) setProblem(t.chat.videoWaitsForNet, true)
                },
                onVorschau: (bild) => aendere(key, { preview: URL.createObjectURL(bild) }),
                ziel: videoZiel(werte),
                // The trip's clock for a recording without a zone (1.101.0,
                // review): the photo beside it had it, the video did not,
                // and its time fell to the device's clock.
                reiseVersatz,
                onRechnet: (anteil) => aendere(key, { stufe: 'rechnet', fortschritt: anteil }),
                // Keine stille Absage: wenn das Gerät nicht umrechnen kann,
                // steht der Grund an der Kachel -- und hochgeladen wird
                // nichts. Der Wurf kommt aus `uploadVideo` und landet im
                // `catch` weiter unten; **dieser** Rückruf ist vorher dran,
                // damit die Zahlen neben der Meldung stehen (#41).
                // **Ein Rückruf, aus dem alles folgt.** Grösse, Grenze,
                // Ausbeute und Hinweis kommen aus demselben Befund -- vorher
                // waren es zwei Rückrufe, und die Grenze wurde nur in einem
                // davon nachgezogen.
                onErgebnis: (befund) =>
                  aendere(key, {
                    // Die wahre Zahl: hier stand die Grösse der **ausgewählten**
                    // Datei, und nach dem Verkleinern war das die Grösse von
                    // etwas, das gar nicht hochgeht.
                    bytes: befund.nachher,
                    // Und die Grenze gilt für das, was hochgeht -- gerechnet
                    // mit derselben Funktion wie beim Auswählen.
                    ueberGrenze: ueberGrenze(befund.nachher, 'video', werte),
                    ausbeute: ausbeuteText(befund) ?? undefined,
                    // Die Dauer kennt der Zerleger -- dafuer braucht es kein
                    // eigenes `<video>`-Element mehr.
                    dauerMs: befund.dauerMs > 0 ? befund.dauerMs : undefined,
                    // Why an HDR recording went out as SDR (1.179.0) stands
                    // where a failed reduction stands: at the tile.
                    hinweis: befund.grund !== '' ? befund.grund : befund.hdrNote !== '' ? befund.hdrNote : undefined,
                  }),
              },
            )
            aendere(key, { stufe: 'fertig', fortschritt: 1, id: medium.id, waitsForNet: false })
          } catch (fehler) {
            // Taken out while waiting: the tile is gone, and what the server
            // has of it goes too, as with a finished one.
            if (cancel.abgebrochen) {
              if (cancel.id !== undefined) discard.mutate({ id: cancel.id })
              return
            }
            aendere(key, {
              stufe: 'fehler',
              waitsForNet: false,
              problem: fehler instanceof Error ? fehler.message : t.bausteine.medien.videoNichtDurch,
            })
          } finally {
            cancels.current.delete(key)
          }
        })
        continue
      }

      if (!isImage(file)) {
        aendere(key, { stufe: 'fehler', problem: t.bausteine.medien.wederBildNochVideo })
        continue
      }

      try {
        aendere(key, { stufe: 'rechnet' })
        const prepared = await prepareImage(file, {
          // **Welches Paar gilt, entscheidet `bildgrenzen()` und nicht diese
          // Zeile** (#59): die Frage gehört einmal beantwortet, nicht an jedem
          // Aufrufer.
          ...bildgrenzen(werte, alsReisender),
          // **Auch hier die Uhr der Reise**, obwohl es „nur" ein Chatfoto ist:
          // die Aufnahmezeit entscheidet später mit, welchem Aufenthalt das
          // Bild zugeordnet wird. Eine Stunde daneben, und es fällt aus dem
          // Fenster.
          reiseVersatz,
        })
        // Into the trace, not the UI (1.21.1): a photo without a capture
        // date later stands as "ohne Datum" in the photo picker, and only
        // here is it known why -- no EXIF block (stripped, forwarded, a
        // screenshot), an unreadable one, or no picture container at all.
        if (prepared.aufnahme.at === null) notiere(`Foto ohne Datum: ${prepared.aufnahme.befund}`)
        // Ab hier zählt das verkleinerte Bild, nicht die Datei aus der
        // Auswahl: das ist die Zahl, die über die Leitung geht.
        aendere(key, {
          bytes: prepared.blob.size,
          ueberGrenze: ueberGrenze(prepared.blob.size, 'bild', werte),
          // Das Bild bleibt hier liegen -- ohne Netz ist es das, was in den
          // Ausgangskorb wandert. Das Vorschaubild mit ihm (1.3.0).
          blob: prepared.blob,
          thumb: prepared.thumb,
        })

        // Ein Bild ohne eigene Vorschau (HEIC) bekommt sie jetzt.
        setFotos((alt) =>
          alt.map((foto) =>
            foto.key === key && foto.preview === null
              ? { ...foto, preview: URL.createObjectURL(prepared.blob) }
              : foto,
          ),
        )

        kette.current = kette.current.then(async () => {
          // Ohne Netz gar nicht erst anfangen: das Bild ist fertig und wartet
          // mit der Nachricht auf später. Gefragt wird `netzStand()` und nicht
          // `navigator.onLine` -- Letzteres sagt „online", sobald irgendein
          // WLAN da ist, auch wenn nichts hindurchgeht.
          if (!netzStand()) {
            aendere(key, { stufe: 'offline' })
            return
          }

          try {
            aendere(key, { stufe: 'lädt', fortschritt: 0 })
            const media = await upload.mutateAsync({
              blob: prepared.blob,
              // Aus dem Original gelesen, bevor verkleinert wurde -- der
              // Server legt Ort und Zeit an der Datei ab.
              aufnahme: prepared.aufnahme,
              // Das Vorschaubild fuer die Kachel (#57) -- es entsteht in
              // `prepareImage()` aus derselben Bitmap.
              thumb: prepared.thumb,
              onProgress: (anteil) => aendere(key, { fortschritt: anteil }),
            })
            aendere(key, { stufe: 'fertig', fortschritt: 1, id: media.id })
          } catch (fehler) {
            // Am Netz gescheitert heißt: später. Alles andere ist ein Fehler,
            // den man sehen muss -- ein Bild über der Grenze etwa geht auch
            // beim zehnten Versuch nicht durch.
            if (fehler instanceof ApiError && fehler.offline) {
              aendere(key, { stufe: 'offline' })
              return
            }
            // Ein gescheitertes Bild hält die anderen nicht auf.
            aendere(key, {
              stufe: 'fehler',
              problem: fehler instanceof Error ? fehler.message : t.bausteine.medien.bildNichtDurch,
            })
          }
        })
      } catch (fehler) {
        aendere(key, {
          stufe: 'fehler',
          problem: fehler instanceof Error ? fehler.message : t.chat.bildNichtGeoeffnet,
        })
      }
    }
  }

  /**
   * Ein GIF, das der Server schon geholt hat.
   *
   * Es hat bereits eine id und liegt bereits in der Reise -- es ist damit an
   * derselben Stelle wie ein fertig hochgeladenes Bild und geht denselben Weg
   * weiter. Ohne `blob`: in den Ausgangskorb gehört es nicht, denn geholt
   * wurde es ohnehin nur, weil gerade Netz da war.
   */
  function nimmGif(media: Media) {
    setProblem(null)
    setFotos((alt) => [
      ...alt,
      {
        key: naechsterSchluessel++,
        preview: media.url,
        stufe: 'fertig' as Stufe,
        fortschritt: 1,
        bytes: media.bytes,
        art: 'bild' as const,
        id: media.id,
      },
    ])
  }

  /**
   * Pictures that hang at a point or spot already (1.52.0, "Aus dieser
   * Reise" in the chat) -- the same shape as a GIF: on the server, with an
   * id, without a blob. Sent, they hang at both afterwards, like a chat
   * photo taken "Auf die Karte" the other way round; the server keeps
   * `highlight_id` on purpose (`MessagesController::attachMedia`).
   */
  function nimmAusReise(medien: Media[]) {
    setProblem(null)
    setFotos((alt) => [
      ...alt,
      ...medien.map((media) => ({
        key: naechsterSchluessel++,
        // A video without its poster has no picture yet (1.78.0): `/poster`
        // would be a 404 in a plain `<img>`, the broken sign where the sent
        // bubble shows none. `null` gives the tile its video sign instead.
        preview: media.kind === 'audio' || (media.kind === 'video' && media.poster_url === null)
          ? null
          : (standbildAdresse(media) ?? media.url),
        stufe: 'fertig' as Stufe,
        fortschritt: 1,
        bytes: media.bytes,
        art: media.kind === 'video' ? ('video' as const) : media.kind === 'audio' ? ('ton' as const) : ('bild' as const),
        id: media.id,
        bestand: true,
        ...(media.duration_ms > 0 ? { dauerMs: media.duration_ms } : {}),
      })),
    ])
  }

  function remove(item: Attachment) {
    const cancel = cancels.current.get(item.key)
    if (cancel !== undefined) cancel.abgebrochen = true
    setFotos((alt) => alt.filter((foto) => foto.key !== item.key))
    // A picture from the trip carries the server's address as its preview,
    // not an object URL of its own -- nothing to revoke there.
    if (item.preview && item.bestand !== true) URL.revokeObjectURL(item.preview)
    // Auf dem Server auch wegräumen, sonst zählt es gegen das Kontingent,
    // bis es von selbst verfällt. **Nicht, was schon zur Reise gehört**
    // (1.52.0): das hängt an einem Punkt, und das Kreuz nimmt es nur aus
    // der Zeile. Ein GIF dagegen ist ohne Beitrag ein Rest, das bleibt so.
    if (item.id !== undefined && item.bestand !== true) discard.mutate({ id: item.id })
  }

  /**
   * A finished recording becomes a tile -- converted, then uploaded.
   *
   * **Why this is here and not with the microphone.** A voice note is the
   * third way an attachment comes in, next to a file and a GIF, and it takes
   * the same road: tile first, then the work, then the upload. Its
   * conversion (`ueberallAbspielbar`) is to a recording what `prepareImage`
   * is to a photo. The microphone's business ends with the blob.
   *
   * Two things go back to the caller instead of into a state here: the
   * sentence about a failed conversion (it belongs under the microphone) and
   * whether the upload worked (the caller may have promised to send).
   */
  async function nimmTon(roh: Aufnahme): Promise<{ ok: boolean; hinweis: string | null }> {
    // **Die Kachel steht da, bevor gerechnet wird** -- dieselbe Reihenfolge
    // wie beim Verkleinern eines Fotos. Wo MP4 herauskam, ist `rechnet` nur
    // ein Wimpernschlag; wo umgewandelt wird, ist es die Auskunft, dass
    // etwas läuft und die Aufnahme nicht verloren ist.
    const key = naechsterSchluessel++
    setFotos((alt) => [
      ...alt,
      {
        key,
        preview: null,
        stufe: 'rechnet' as Stufe,
        fortschritt: 0,
        bytes: roh.blob.size,
        art: 'ton' as const,
        dauerMs: roh.dauerMs,
      },
    ])

    const fertig = await ueberallAbspielbar(roh)
    // Nur noch die letzte Rückfalllinie: das Umwandeln selbst ist
    // gescheitert. Hochgeladen wird trotzdem, und der Satz geht an den
    // Aufrufer -- er steht am Mikrofon und nicht an der Kachel.
    const hinweis = fertig.ueberall ? null : t.chat.formatStumm
    /*
     * **The recording stays at the tile** (1.80.0), like a photo's bytes:
     * without a net it goes into the outbox with the post and up from there.
     * Until here only the id was kept, and a recording made offline was gone
     * the moment its upload failed. From use: "Sprachnachrichten auch offline
     * möglich machen".
     */
    aendere(key, { bytes: fertig.blob.size, blob: fertig.blob, dauerMs: fertig.dauerMs })
    if (!netzStand()) {
      aendere(key, { stufe: 'offline' })
      return { ok: true, hinweis }
    }
    aendere(key, { stufe: 'lädt' })

    try {
      const medium = await api.upload<Media>(
        `/api/trips/${tripId}/media/voice`,
        fertig.blob,
        (anteil) => aendere(key, { fortschritt: anteil }),
        { 'x-dauer-ms': String(Math.round(fertig.dauerMs)) },
      )
      aendere(key, { stufe: 'fertig', fortschritt: 1, id: medium.id })
      return { ok: true, hinweis }
    } catch (fehler) {
      // At the net means later, from the basket -- the bytes are at the tile.
      if (fehler instanceof ApiError && fehler.offline) {
        aendere(key, { stufe: 'offline' })
        return { ok: true, hinweis }
      }
      aendere(key, {
        stufe: 'fehler',
        problem: fehler instanceof Error ? fehler.message : t.chat.aufnahmeNichtDurch,
      })
      return { ok: false, hinweis }
    }
  }

  /** Nach dem Absenden: Kacheln weg, Vorschauen freigegeben. */
  function leeren() {
    fotos.forEach((foto) => foto.preview && URL.revokeObjectURL(foto.preview))
    setFotos([])
    setProblem(null)
  }

  return {
    fotos,
    /** Geht mit hinaus, weil der Vorrat beim Ortswechsel die Liste setzt. */
    setFotos,
    problem,
    problemOhneNetz,
    setProblem,
    angeheftet,
    beschaeftigt,
    fertige,
    wartet,
    aendere,
    add,
    nimmGif,
    nimmAusReise,
    nimmTon,
    entferne: remove,
    leeren,
  }
}

/** Ob an dieser Kachel gerade gearbeitet wird -- Warten auf Netz zählt nicht. */
export function unterwegs(stufe: Stufe): boolean {
  return stufe === 'wartet' || stufe === 'rechnet' || stufe === 'lädt'
}
